import { API_BASE } from '../constants';

/**
 * 이미지에 "cartech" 워터마크를 주사위 5 모양으로 삽입
 * @param {string} imageSrc - 원본 이미지 URL (dataURL, http URL 등)
 * @param {Object} options - 옵션
 * @param {string} options.text - 워터마크 텍스트 (기본: "cartech")
 * @param {number} options.alpha - 투명도 0~1 (기본: 0.2)
 * @param {string} options.color - 글자색 (기본: "#FFFFFF")
 * @param {string} options.shadowColor - 그림자색 (기본: "rgba(0,0,0,0.6)")
 * @param {number} options.shadowBlur - 그림자 흐림 정도 (기본: 6)
 * @returns {Promise<string>} - 워터마크가 삽입된 이미지 dataURL
 */
export async function addWatermark(imageSrc: string, options: {
  text?: string;
  alpha?: number;
  color?: string;
  shadowColor?: string;
  shadowBlur?: number;
} = {}): Promise<string> {
  const {
    text = 'cartech',
    alpha = 0.2,
    color = '#FFFFFF',
    shadowColor = 'rgba(0, 0, 0, 0.6)',
    shadowBlur = 6,
  } = options;

  // 1. 이미지 소스 준비
  let img: HTMLImageElement;

  if (imageSrc.startsWith('http')) {
    // 외부 URL: fetch로 프록시를 통해 blob 가져오기 → blob URL로 Image 로드
    const proxyUrl = `${API_BASE}/api/proxy-image?url=${encodeURIComponent(imageSrc)}`;
    const res = await fetch(proxyUrl);
    if (!res.ok) {
      throw new Error(`Proxy fetch failed: ${res.status} ${res.statusText}`);
    }
    const blob = await res.blob();
    if (!blob.type.startsWith('image/')) {
      throw new Error(`Proxy returned non-image content: ${blob.type}`);
    }
    const blobUrl = URL.createObjectURL(blob);
    try {
      const loadedImg = new Image();
      await new Promise<void>((resolve, reject) => {
        loadedImg.onload = () => resolve();
        loadedImg.onerror = () => reject(new Error('Failed to decode image'));
        loadedImg.src = blobUrl;
      });
      img = loadedImg;
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
  } else {
    // dataURL 또는 상대 경로: Image 객체로 로드
    const loadedImg = new Image();
    await new Promise<void>((resolve, reject) => {
      loadedImg.onload = () => resolve();
      loadedImg.onerror = () => reject(new Error('Failed to load image'));
      loadedImg.src = imageSrc;
    });
    img = loadedImg;
  }

  // 2. Canvas 설정
  const canvas = document.createElement('canvas');
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  canvas.width = w;
  canvas.height = h;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get canvas context');
  }
  ctx.drawImage(img, 0, 0, w, h);

  // 3. 워터마크 그리기
  ctx.save();
  ctx.font = `bold ${Math.min(w, h) / 10}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(0.7, Math.min(w, h) / 750);
  ctx.globalAlpha = alpha;

  ctx.shadowColor = shadowColor;
  ctx.shadowBlur = shadowBlur;

  const positions = [
    { x: w * 0.15, y: h * 0.15 },
    { x: w * 0.85, y: h * 0.15 },
    { x: w * 0.50, y: h * 0.50 },
    { x: w * 0.15, y: h * 0.85 },
    { x: w * 0.85, y: h * 0.85 },
  ];

  for (const pos of positions) {
    ctx.strokeText(text, pos.x, pos.y);
  }
  ctx.restore();

  // 4. 결과 반환 (webp优先, 실패시 png)
  try {
    return canvas.toDataURL('image/webp', 0.9);
  } catch {
    return canvas.toDataURL('image/png');
  }
}
